import { forwardRef } from 'react';

import { InfoButton } from 'components/InfoButton';
import { Skeleton } from 'components/Skeleton';
import { cn } from 'components/utils';

import {
  metricCardCaptionRecipe,
  metricCardFooterRecipe,
  metricCardIconRecipe,
  metricCardInfoRecipe,
  metricCardListRecipe,
  metricCardPlaceholderRecipe,
  metricCardRecipe,
  metricCardSkeletonRecipe,
  metricCardTitleLabelRecipe,
  metricCardTitleRecipe,
  metricCardValueRecipe,
} from './MetricCard.recipe';

import type { MetricCardProps } from './types';

/**
 * One labelled number, from Figma set 2037:134558. Static: only the InfoButton and whatever the
 * caller puts in the footer take focus.
 *
 * The title, value and caption are a `dl`, so a row of metrics reads "Started, 412, 33% of
 * invited" with no heading noise. The footer sits outside the list because a `dl` may only hold
 * name-value groups.
 *
 * No `aria-live`: a value that updates every second (backstage) would talk over everything else.
 */
export const MetricCard = forwardRef<HTMLDivElement, MetricCardProps>(
  (
    {
      title,
      value,
      caption,
      info,
      infoLabel,
      icon,
      layout = 'tile',
      surface = 'filled',
      loading = false,
      footer,
      className,
      ...rest
    },
    ref
  ) => {
    if (title.trim() === '') {
      throw new Error('MetricCard requires a non-empty title.');
    }

    const hasIcon = icon !== undefined && icon !== null && icon !== false;
    const hasInfo = info !== undefined && infoLabel !== undefined;
    const showCaption = !loading && caption !== undefined && caption !== '';
    const showFooter =
      layout === 'tile' && !loading && footer !== undefined && footer !== null && footer !== false && footer !== '';

    return (
      <div
        ref={ref}
        {...rest}
        aria-busy={loading || undefined}
        className={cn(metricCardRecipe({ layout, surface }), className)}
        data-layout={layout}
        data-loading={loading || undefined}
        data-part="root"
        data-surface={surface}
      >
        <dl className={metricCardListRecipe({ layout })}>
          <dt className={metricCardTitleRecipe({ layout })} data-part="title">
            {hasIcon && (
              <span aria-hidden="true" className={metricCardIconRecipe()} data-part="icon">
                {icon}
              </span>
            )}
            <span className={metricCardTitleLabelRecipe()}>
              {title}
              {/* A word joiner glues the button to the last word; overflow-wrap can still break inside it. */}
              {hasInfo && '\u2060'}
              {hasInfo && (
                <span className={metricCardInfoRecipe()} data-part="info">
                  <InfoButton aria-label={infoLabel} label={info} />
                </span>
              )}
            </span>
          </dt>

          <dd className={metricCardValueRecipe({ layout, hasCaption: showCaption })} data-part="value">
            {loading ? (
              <span aria-hidden="true" className={metricCardPlaceholderRecipe({ layout })} data-part="placeholder">
                <Skeleton shape="block" className={metricCardSkeletonRecipe({ layout })} />
              </span>
            ) : (
              value
            )}
          </dd>

          {showCaption && (
            <dd className={metricCardCaptionRecipe({ layout, hasIcon })} data-part="caption">
              {caption}
            </dd>
          )}
        </dl>

        {showFooter && (
          <div className={metricCardFooterRecipe()} data-part="footer">
            {footer}
          </div>
        )}
      </div>
    );
  }
);
